// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useRoundState } from '@/stores/roundState';

const STORAGE_KEY = 'goffy:current-tour';
let memoryTourId: string | null = null;

type CurrentTourListener = (tourId: string | null) => void;
const currentTourListeners = new Set<CurrentTourListener>();

const getStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const notifyCurrentTourListeners = () => {
  currentTourListeners.forEach((listener) => {
    listener(memoryTourId);
  });
};

// Native has no synchronous storage, so the id is restored in the background and
// subscribers are notified once it lands. A tour selected before the read resolves
// wins, which is why this only fills an id that is still unset.
if (!getStorage() && asyncStorage) {
  void asyncStorage
    .getItem(STORAGE_KEY)
    .then((stored) => {
      if (!stored || memoryTourId !== null) return;
      memoryTourId = stored;
      notifyCurrentTourListeners();
    })
    .catch(() => undefined);
}

/**
 * Subscribe to changes of the globally-remembered current tour id. Components
 * that gate behavior on the current tour (e.g. the bottom menu) must use this so
 * they react when another screen updates the tour via setCurrentTourId — reading
 * getCurrentTourId() alone is not reactive.
 */
export function subscribeCurrentTour(listener: CurrentTourListener): () => void {
  currentTourListeners.add(listener);
  return () => {
    currentTourListeners.delete(listener);
  };
}

export function setCurrentTourId(tourId: string | null, options?: { preserveRound?: boolean }) {
  const current = getCurrentTourId();
  if (tourId && tourId !== current && !options?.preserveRound) {
    const roundState = useRoundState.getState();
    roundState.setRoundAndHole(null, null);
  }
  const changed = tourId !== current;
  memoryTourId = tourId;
  const storage = getStorage();
  if (storage) {
    if (tourId) {
      storage.setItem(STORAGE_KEY, tourId);
    } else {
      storage.removeItem(STORAGE_KEY);
    }
  } else if (asyncStorage) {
    void (tourId
      ? asyncStorage.setItem(STORAGE_KEY, tourId)
      : asyncStorage.removeItem(STORAGE_KEY)
    ).catch(() => undefined);
  }
  if (changed) {
    notifyCurrentTourListeners();
  }
}

export function getCurrentTourId(): string | null {
  const storage = getStorage();
  if (!storage) return memoryTourId;
  return storage.getItem(STORAGE_KEY) ?? memoryTourId;
}
